import {useEffect, useState} from 'react';
import {
  COMPANY_PAGE_TYPE_NONE,
  COMPANY_PAGE_TYPE_STUDY,
} from '../../../core/entities/companyDetail/constans';
import type {GroupCompanies} from '../../../core/entities/companyDetail/schema';
import type {GetCompanyInfoResponse} from '../../../core/repositories/companiesRepository';
import type {
  GetCompanyDetailResponse,
  GetCompanyPageColumnPostsResponse,
  GetNativeAdPostsResponse,
  GetRecruitmentsResponse,
} from '../../../core/repositories/companyDetailRepository';
import type {Company} from '../../../core/repositories/companyRepository';
import {useGetMeInterests} from '../../../core/repositories/mypageRepository';
import type {Recruitment} from '../../../core/repositories/recruitingInfoRepository';
import {useHandleFollowCompany} from '../../../core/usecases/useHandleFollowCompany';
import {convertTrackingEntryURL} from '../../../shared/services/convertTrackingEntryURL';
import {
  canShowFollowCompletionPrompt,
  suppressFollowCompletionPromptIfChecked,
} from './followCompletionPromptSuppression';
import {useScrollRestoration} from './useScrollRestoration';

// ヘッダーの見出し情報
export type Heading = {
  id: string;
  tagname: string;
};

export type IsContents = {
  isIntrodution: boolean;
  isWebSeminar: boolean;
  isInterview: boolean;
  isOfficeGallery: boolean;
  isNativeAds: boolean;
  isSpecialColumn: boolean;
  isRecruitmentGuideline: boolean;
  isCompanyData: boolean;
  isRelatedCommunities: boolean;
  isGroupCompanies: boolean;
  isRecruitments: boolean;
  isOfferedUsersAttribute: boolean;
  isFollowUsersAttribute: boolean;
  isSameIndustryCompanies: boolean;
};

type Props = {
  companyDetail: GetCompanyDetailResponse;
  companyInfo: GetCompanyInfoResponse;
  nativeAds?: GetNativeAdPostsResponse;
  companyPageColumnPosts?: GetCompanyPageColumnPostsResponse;
  recruitments?: GetRecruitmentsResponse;
  sameIndustryCompanies?: Company[];
  groupCompanies?: GroupCompanies;
  isPreview?: boolean;
  isValid: boolean;
  isPC: boolean;
  sourceURL?: string;
};

export const useCompanyDetailContents = ({
  companyDetail,
  companyInfo,
  nativeAds,
  companyPageColumnPosts,
  recruitments,
  sameIndustryCompanies,
  groupCompanies,
  isPreview,
  isValid,
  isPC,
  sourceURL,
}: Props) => {
  const filteredRecruitments = recruitments?.recruitments.filter(
    (recruitment) =>
      recruitment.targetYear === companyDetail.metaData?.graduationYear ||
      recruitment.targetYear === 0,
  ) as Recruitment[];

  const isContents: IsContents = checkContents({
    companyDetail,
    filteredRecruitments,
    isValid,
    sameIndustryCompanies,
    companyPageColumnPosts,
    groupCompanies,
    nativeAds,
    isPreview,
    isPC,
  });

  const entryURLForTracking = convertTrackingEntryURL(
    companyDetail.metaData.companyName,
    companyDetail.metaData.companyID,
    isPC ? 'pc' : 'sp',
    companyDetail.metaData.companyYearInfoID,
    companyDetail.metaData.entryURL,
    sourceURL,
  );
  const recruitingFairRegistrationURLForTracking = convertTrackingEntryURL(
    companyDetail.metaData.companyName,
    companyDetail.metaData.companyID,
    isPC ? 'pc' : 'sp',
    companyDetail.metaData.companyYearInfoID,
    companyDetail.metaData.recruitingFairRegistrationURL,
    sourceURL,
  );
  const [followCompletionModalOpened, setFollowCompletionModalOpened] =
    useState(false);
  const [followCompletionSuppressChecked, setFollowCompletionSuppressChecked] =
    useState(false);

  const closeFollowCompletionModal = () => {
    suppressFollowCompletionPromptIfChecked(followCompletionSuppressChecked);
    setFollowCompletionModalOpened(false);
  };

  const {followCountUpdated, isFollowed, handleFollowCompany} =
    useHandleFollowCompany({
      companyId: companyInfo.id,
      followCount: companyInfo.followerCount,
      followed: companyInfo.followed,
      onFollowSuccess() {
        if (canShowFollowCompletionPrompt()) {
          setFollowCompletionModalOpened(true);
        }
      },
    });

  // 企業詳細コンテンツの見出し(h2)のアンカーリンク用
  // コンテンツによりない場合もある動的な値
  const headerContents: Heading[] = [];
  if (isContents.isIntrodution) {
    headerContents.push({id: 'introduction', tagname: '企業紹介'});
  }

  if (isContents.isNativeAds) {
    headerContents.push({id: 'native-ads', tagname: '特集記事'});
  }

  if (isContents.isWebSeminar && isValid) {
    // 動画はログインしている場合のみ表示
    headerContents.push({id: 'web-seminar', tagname: '動画'});
  }

  if (isContents.isInterview) {
    headerContents.push({id: 'interview', tagname: '社員紹介'});
  }

  if (isContents.isOfficeGallery) {
    headerContents.push({id: 'office-gallery', tagname: 'ギャラリー'});
  }

  if (isContents.isRecruitmentGuideline) {
    headerContents.push({id: 'recruitment-guide', tagname: '実施要項'});
  }

  if (isContents.isCompanyData) {
    headerContents.push({id: 'company-data', tagname: '会社データ'});
  }

  if (isContents.isSpecialColumn) {
    headerContents.push({id: 'special-column', tagname: '企業研究'});
  }

  if (isContents.isRecruitments) {
    headerContents.push({id: 'recruitment', tagname: '募集情報'});
  }

  if (isContents.isOfferedUsersAttribute) {
    headerContents.push({
      id: 'offered_users_attribute',
      tagname: '内定者属性',
    });

    if (isContents.isFollowUsersAttribute) {
      headerContents.push({
        id: 'follow_users_attribute',
        tagname: 'フォロワーの属性',
      });
    }

    if (isContents.isGroupCompanies) {
      headerContents.push({id: 'group_companies', tagname: '関連企業'});
    }
  }

  const [screenWidth, setScreenWidth] = useState(0);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setScreenWidth(window.innerWidth);
    }
  }, []);

  useScrollRestoration();

  const {data} = useGetMeInterests({
    limit: 10_000,
    config: {enabled: isValid},
  });
  const interestsId = data?.ok
    ? data.value.interests.find(
        (interest) =>
          interest.companyID === companyDetail.companyData?.companyID &&
          interest.status === 'unread',
      )?.id
    : undefined;

  return {
    isContents,
    filteredRecruitments,
    entryURLForTracking,
    recruitingFairRegistrationURLForTracking,
    followCountUpdated,
    isFollowed,
    handleFollowCompany,
    headerContents,
    screenWidth,
    data,
    interestsId,
    followCompletionModalOpened,
    closeFollowCompletionModal,
    followCompletionSuppressChecked,
    setFollowCompletionSuppressChecked,
  };
};

const checkContents = ({
  companyDetail,
  filteredRecruitments,
  isValid,
  nativeAds,
  companyPageColumnPosts,
  sameIndustryCompanies,
  groupCompanies,
  isPreview,
  isPC,
}: {
  companyDetail: GetCompanyDetailResponse;
  filteredRecruitments: Recruitment[];
  isValid: boolean;
  nativeAds?: GetNativeAdPostsResponse;
  companyPageColumnPosts?: GetCompanyPageColumnPostsResponse;
  sameIndustryCompanies?: Company[];
  groupCompanies?: GroupCompanies;
  isPreview?: boolean;
  isPC?: boolean;
}) => {
  return {
    isIntrodution: checkIntroduction(companyDetail),
    isWebSeminar: checkWebSeminar(companyDetail),
    isInterview: checkInterview(companyDetail),
    isOfficeGallery: checkOfficeGallery(companyDetail),
    isNativeAds: checkNativeAds(nativeAds),
    isSpecialColumn: checkSpecialColumn(companyPageColumnPosts),
    isRecruitmentGuideline: checkRecruitmentGuideline(
      companyDetail,
      isValid,
      isPreview,
    ),
    isCompanyData: checkCompanyData(companyDetail),
    isRelatedCommunities: checkRelatedCommunities(companyDetail, isValid),
    isGroupCompanies: checkGroupCompanies(groupCompanies, isPC),
    isRecruitments: checkRecruitments(filteredRecruitments),
    isOfferedUsersAttribute: checkOfferedUsersAttribute(companyDetail),
    isFollowUsersAttribute: checkFollowUsersAttribute(companyDetail),
    isSameIndustryCompanies: checkSameIndustryCompanies(
      sameIndustryCompanies,
      companyDetail,
      isValid,
    ),
  };
};

const checkIntroduction = (companyDetail: GetCompanyDetailResponse) =>
  companyDetail.introduction !== undefined;

const checkWebSeminar = (companyDetail: GetCompanyDetailResponse) =>
  companyDetail.webSeminar !== undefined;

const checkInterview = (companyDetail: GetCompanyDetailResponse) =>
  companyDetail.companyInterview !== undefined;

const checkOfficeGallery = (companyDetail: GetCompanyDetailResponse) =>
  companyDetail.officeGallery !== undefined;

const checkNativeAds = (nativeAds?: GetNativeAdPostsResponse) =>
  nativeAds !== undefined && 'posts' in nativeAds && nativeAds.posts.length > 0;

const checkSpecialColumn = (
  companyPageColumnPosts?: GetCompanyPageColumnPostsResponse,
) =>
  companyPageColumnPosts?.posts !== undefined &&
  companyPageColumnPosts.posts.length > 0;

const checkRecruitmentGuideline = (
  companyDetail: GetCompanyDetailResponse,
  isValid: boolean,
  isPreview?: boolean,
) => {
  return (
    (companyDetail.recruitmentGuideline !== undefined &&
      (isValid || isPreview) &&
      (companyDetail.metaData?.entryURL !== undefined ||
        companyDetail.metaData?.recruitingFairRegistrationURL !== undefined)) ||
    false
  );
};

const checkCompanyData = (companyDetail: GetCompanyDetailResponse) =>
  companyDetail.companyData !== undefined;

const checkRelatedCommunities = (
  companyDetail: GetCompanyDetailResponse,
  isValid: boolean,
) => Boolean(companyDetail.relatedCommunity?.relatedCommunities) && isValid;

const checkGroupCompanies = (groupCompanies?: GroupCompanies, isPC?: boolean) =>
  (groupCompanies !== undefined && groupCompanies.length > 0 && isPC) || false;

const checkRecruitments = (filteredRecruitments: Recruitment[]) =>
  (filteredRecruitments && filteredRecruitments?.length > 0) || false;

const checkOfferedUsersAttribute = (companyDetail: GetCompanyDetailResponse) =>
  companyDetail.offeredUsersAttribute !== undefined &&
  companyDetail.offeredUsersAttribute.usersCount > 4;

const checkFollowUsersAttribute = (companyDetail: GetCompanyDetailResponse) =>
  (companyDetail.followUsersAttribute &&
    companyDetail.followUsersAttribute.usersCount > 4) ||
  false;

const checkSameIndustryCompanies = (
  sameIndustryCompanies?: Company[],
  companyDetail?: GetCompanyDetailResponse,
  isValid?: boolean,
) =>
  (sameIndustryCompanies !== undefined &&
    sameIndustryCompanies.length > 0 &&
    (companyDetail?.metaData?.companyPlan === COMPANY_PAGE_TYPE_NONE ||
      companyDetail?.metaData?.companyPlan === COMPANY_PAGE_TYPE_STUDY) &&
    isValid) ??
  false;
